// 189 §B / §C / §D — THE THREE CRM DEFINITION KINDS, ONE HOME BOTH ENDS LOAD.
//
// A custom field (167 R7), a stage set and a playbook (167 R8) are each a
// TaxonomyEntry row — kind `custom_field` / `stage_set` / `playbook` — whose
// `meta` carries the part a label cannot: the field's type and list, the
// set's stages, the playbook's trigger and task templates. The forms render
// from the definitions, the routes validate against them, the playbook fires
// from them; so the meta's shape is normalised HERE, once, and every reader
// (SettingsTaxonomies' editors, CustomFieldsBlock, the three entity routes,
// lib/playbooks.js) calls the same functions. Authored as `.mjs` for 181 A.1's
// reason: pure ESM the client imports and the server `require()`s (Node ≥
// 20.19; folderName.mjs, sharePointNames.mjs and collectiveSegments.mjs are
// the precedents).
//
// Pure: no React, no DOM, no database. A "def" below is a served taxonomy row
// (`{ code, label, labelEn, labelFr, labelNl, order, archived, meta }`).

// The JSDoc below is what a TypeScript reader sees (tsconfig allowJs, checkJs
// off): without it every export is `any` at the import and a zero-warning
// consumer cannot exist.
/** @typedef {'text'|'number'|'date'|'bool'|'select'} CustomFieldType */
/** @typedef {{ code: string, label: string }} CustomFieldOption */
/** @typedef {{ type: CustomFieldType, entities: string[], options: CustomFieldOption[], showInList: boolean }} CustomFieldMeta */
/** @typedef {{ code?: string|null, label?: string|null, labelEn?: string|null, labelFr?: string|null, labelNl?: string|null, order?: number|null, archived?: boolean|null, meta?: unknown }} DefRow */
/** @typedef {{ id: number, type: string, by: string, juId: number|null, notes: string, startDate: string, registerNumber: string }} StageElement */
/** @typedef {{ title: string, assignee: string, dueDays: number, type: string|null }} PlaybookTask */
/** @typedef {{ trigger: string, stageType: string, tasks: PlaybookTask[] }} PlaybookMeta */

// ── §B custom fields ────────────────────────────────────────────────────────

/** @type {readonly CustomFieldType[]} */
export const CUSTOM_FIELD_TYPES = ['text', 'number', 'date', 'bool', 'select'];
/** @type {readonly string[]} */
export const CUSTOM_FIELD_ENTITIES = ['client', 'case', 'cc'];

/** @param {unknown} v @returns {unknown[]} */
const asArray = (v) => (Array.isArray(v) ? v : []);
/** @param {unknown} v @returns {string} */
const asString = (v) => (typeof v === 'string' ? v : v == null ? '' : String(v));
/** @param {unknown} s @returns {string} */
const slug = (s) => asString(s).trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');

/**
 * @param {unknown} v
 * @returns {CustomFieldType}
 */
export function asCustomFieldType(v) {
  return /** @type {CustomFieldType} */ (typeof v === 'string' && CUSTOM_FIELD_TYPES.includes(/** @type {CustomFieldType} */ (v)) ? v : 'text');
}

/**
 * The meta of a `custom_field` row, every part present and well-typed.
 * @param {unknown} meta
 * @returns {CustomFieldMeta}
 */
export function normalizeCustomFieldMeta(meta) {
  const m = /** @type {Record<string, any>} */ (meta && typeof meta === 'object' ? meta : {});
  const type = asCustomFieldType(m.type);
  const entities = /** @type {string[]} */ ([...new Set(asArray(m.entities).filter((e) => typeof e === 'string' && CUSTOM_FIELD_ENTITIES.includes(e)))]);
  const options = /** @type {any[]} */ (asArray(m.options))
    .map((o) => (o && typeof o === 'object' ? { code: slug(o.code || o.label), label: asString(o.label).trim() } : { code: slug(o), label: asString(o).trim() }))
    .filter((o) => o.code);
  return { type, entities, options: type === 'select' ? options : [], showInList: m.showInList === true };
}

/**
 * The definitions that apply to one entity kind, in taxonomy order, archived ones out.
 * @param {unknown} defs
 * @param {string} entity
 * @returns {DefRow[]}
 */
export function customFieldDefsFor(defs, entity) {
  return /** @type {DefRow[]} */ (asArray(defs))
    .filter((d) => d && !d.archived && d.code && !(d.meta && typeof d.meta === 'object' && /** @type {Record<string, unknown>} */ (d.meta).divider))
    .filter((d) => normalizeCustomFieldMeta(d.meta).entities.includes(entity))
    .sort((a, b) => (a.order || 0) - (b.order || 0));
}

/**
 * One typed value from what a form or a request body hands over. `''`, null
 * and undefined clear the field (→ null). Returns `{ ok: true, value }` or
 * `{ ok: false }` when the raw value cannot be that type — a word in a number
 * field, a code the list does not hold.
 * @param {DefRow|null|undefined} def
 * @param {unknown} raw
 * @returns {{ ok: true, value: string|number|boolean|null } | { ok: false }}
 */
export function coerceCustomFieldValue(def, raw) {
  const meta = normalizeCustomFieldMeta(def && def.meta);
  if (raw === '' || raw == null) return { ok: true, value: null };
  switch (meta.type) {
    case 'number': {
      const n = typeof raw === 'number' ? raw : Number(String(raw).replace(',', '.'));
      return Number.isFinite(n) ? { ok: true, value: n } : { ok: false };
    }
    case 'date': {
      const s = asString(raw).slice(0, 10);
      return /^\d{4}-\d{2}-\d{2}$/.test(s) && !Number.isNaN(Date.parse(s)) ? { ok: true, value: s } : { ok: false };
    }
    case 'bool': {
      if (raw === true || raw === 'true' || raw === 1 || raw === '1') return { ok: true, value: true };
      if (raw === false || raw === 'false' || raw === 0 || raw === '0') return { ok: true, value: false };
      return { ok: false };
    }
    case 'select': {
      const code = asString(raw);
      return meta.options.some((o) => o.code === code) ? { ok: true, value: code } : { ok: false };
    }
    default: {
      const s = asString(raw).trim();
      return { ok: true, value: s || null };
    }
  }
}

/**
 * The values object a route may store for one entity: only codes the
 * definitions know for that entity, each coerced. Unknown codes are dropped
 * (a definition that was deleted, a typo); a value of the wrong type is
 * reported by code and nothing is stored.
 * @param {unknown} defs
 * @param {string} entity
 * @param {unknown} raw
 * @returns {{ ok: boolean, values: Record<string, string|number|boolean>|undefined, bad: string[] }}
 */
export function validateCustomFields(defs, entity, raw) {
  /** @type {Record<string, string|number|boolean>} */
  const out = {};
  /** @type {string[]} */
  const bad = [];
  if (raw === undefined) return { ok: true, values: undefined, bad };
  const src = /** @type {Record<string, unknown>} */ (raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {});
  for (const def of customFieldDefsFor(defs, entity)) {
    const code = String(def.code);
    if (!Object.prototype.hasOwnProperty.call(src, code)) continue;
    const r = coerceCustomFieldValue(def, src[code]);
    if (!r.ok) { bad.push(code); continue; }
    if (r.value !== null) out[code] = r.value;
  }
  return { ok: bad.length === 0, values: out, bad };
}

/**
 * The label a person reads for a definition, in the app's language.
 * @param {DefRow|null|undefined} def
 * @param {string} lang
 * @returns {string}
 */
export function customFieldLabel(def, lang) {
  if (!def) return '';
  /** @type {Record<string, string|null|undefined>} */
  const byLang = { fr: def.labelFr, nl: def.labelNl, en: def.labelEn };
  return asString(byLang[lang] || def.labelEn || def.label || def.code);
}

// ── §C stage sets ───────────────────────────────────────────────────────────

/**
 * The meta of a `stage_set` row: an ordered list of `case_stage` codes.
 * @param {unknown} meta
 * @returns {{ stages: { type: string, by: string }[] }}
 */
export function normalizeStageSetMeta(meta) {
  const m = /** @type {Record<string, any>} */ (meta && typeof meta === 'object' ? meta : {});
  const stages = /** @type {any[]} */ (asArray(m.stages))
    .map((s) => (s && typeof s === 'object' ? { type: asString(s.type), by: asString(s.by) } : { type: asString(s), by: '' }))
    .filter((s) => s.type);
  return { stages };
}

/**
 * The stage elements a set APPENDS to a case's `stages` Json (the shape every
 * reader of that column already reads: id · type · by · juId · notes ·
 * startDate · registerNumber — measured on staging, brief 189 §C). Ids
 * continue from the largest the case already holds — or from `firstId` when
 * the caller's own counter is larger (App's `nid()` numbers stage elements over
 * the whole loaded pool, and the board and the modal hand that in so a stage
 * the set adds is numbered the way saveStageCore would number it); nothing
 * existing moves.
 * @param {unknown} setMeta
 * @param {unknown} existingStages
 * @param {string} startDate
 * @param {number} [firstId]
 * @returns {StageElement[]}
 */
export function stageElementsFromSet(setMeta, existingStages, startDate, firstId) {
  const existing = /** @type {any[]} */ (asArray(existingStages));
  let nextId = existing.reduce((m, s) => Math.max(m, Number(s && s.id) || 0), 0);
  if (Number.isFinite(Number(firstId)) && Number(firstId) - 1 > nextId) nextId = Number(firstId) - 1;
  return normalizeStageSetMeta(setMeta).stages.map((s) => ({
    id: ++nextId,
    type: s.type,
    by: s.by || '',
    juId: null,
    notes: '',
    startDate: asString(startDate),
    registerNumber: '',
  }));
}

/**
 * THE STAGE A CASE IS "IN" — the board's one derivation (189 §C). A case's
 * `stages` is its procedural HISTORY (appeal after first instance after …),
 * so the current stage is the latest one: the greatest `startDate`, and among
 * equals (or dateless ones) the greatest `id` — the order the Stages section
 * itself lists them in. A case with no stage is in no column but the first.
 * @param {unknown} stages
 * @returns {StageElement|null}
 */
export function currentStageOf(stages) {
  const list = /** @type {any[]} */ (asArray(stages)).filter((s) => s && typeof s === 'object' && s.type);
  if (!list.length) return null;
  return list.reduce((best, s) => {
    if (!best) return s;
    const a = asString(s.startDate).slice(0, 10), b = asString(best.startDate).slice(0, 10);
    if (a !== b) return a > b ? s : best;
    return (Number(s.id) || 0) > (Number(best.id) || 0) ? s : best;
  }, null);
}

// ── §H segments ─────────────────────────────────────────────────────────────

/** @typedef {{ archived: 'exclude'|'only'|'any', prospects: 'exclude'|'only'|'any', clientTypes: string[], languages: string[], customFields: { code: string, value: string|number|boolean }[] }} SegmentFilter */

const TRI = ['exclude', 'only', 'any'];
/** @param {unknown} v @param {'exclude'|'only'|'any'} dflt @returns {'exclude'|'only'|'any'} */
const tri = (v, dflt) => (typeof v === 'string' && TRI.includes(v) ? /** @type {'exclude'|'only'|'any'} */ (v) : dflt);

/**
 * The meta.filter of a `mail_segment` row, every part present and well-typed:
 * archived people out by default, prospects in by default, any type and any
 * language unless the lists say otherwise, and an equality per custom-field
 * code (the value as the editor stored it).
 * @param {unknown} meta   the row's meta, or the filter itself
 * @returns {SegmentFilter}
 */
export function normalizeSegmentFilter(meta) {
  const m = /** @type {Record<string, any>} */ (meta && typeof meta === 'object' ? meta : {});
  const f = /** @type {Record<string, any>} */ (m.filter && typeof m.filter === 'object' ? m.filter : m);
  const strings = (v) => /** @type {string[]} */ ([...new Set(asArray(v).filter((x) => typeof x === 'string' && x))]);
  const customFields = /** @type {any[]} */ (asArray(f.customFields))
    .filter((c) => c && typeof c === 'object' && typeof c.code === 'string' && c.code)
    .map((c) => ({ code: String(c.code), value: typeof c.value === 'number' || typeof c.value === 'boolean' ? c.value : asString(c.value) }))
    .filter((c) => c.value !== '');
  return {
    archived: tri(f.archived, 'exclude'),
    prospects: tri(f.prospects, 'any'),
    clientTypes: strings(f.clientTypes),
    languages: strings(f.languages),
    customFields,
  };
}

// ── §D playbooks ────────────────────────────────────────────────────────────

/**
 * THE TWO CODED TRIGGERS (189 §D: two, no rule engine, ever), chosen by what
 * the app already fires on — measured in the §D commit:
 *   case_created  the moment a matter comes into being for the firm — POST
 *                 /cases for a non-prospect, and POST /cases/:id/convert when a
 *                 prospect becomes one (both chronicle `create` /
 *                 `prospect-convert` on the Case today);
 *   stage_added   a stage element joining `Case.stages` — a PUT that appends
 *                 one, or §C's stage-set apply — the write the case chronicle
 *                 already reports field by field.
 */
export const PLAYBOOK_TRIGGERS = ['case_created', 'stage_added'];

/** Who a template's task is assigned to, resolved at fire time on the case. */
export const PLAYBOOK_ASSIGNEES = ['responsible_partner', 'case_team', 'creator', 'nobody'];

/**
 * 208 §13 — A TASK WHOSE TITLE IS EMPTIED STAYS, EMPTY, UNTIL IT IS REMOVED —
 * in the EDITOR, which draws from this function: it read the firing's form, so
 * emptying a title took the task out of its list under the caret (207C C5).
 * `keepUntitled` is the editor's form; the firing (server/src/lib/playbooks.js)
 * reads the default and skips a task with no title, as it always did — a task
 * is never created without one.
 * @param {unknown} meta
 * @param {{ keepUntitled?: boolean }} [opts]
 * @returns {PlaybookMeta}
 */
export function normalizePlaybookMeta(meta, opts) {
  const m = /** @type {Record<string, any>} */ (meta && typeof meta === 'object' ? meta : {});
  const trigger = PLAYBOOK_TRIGGERS.includes(m.trigger) ? m.trigger : 'case_created';
  const tasks = /** @type {any[]} */ (asArray(m.tasks))
    .map((t) => (t && typeof t === 'object' ? t : { title: t }))
    .map((t) => ({
      title: asString(t.title).trim(),
      assignee: PLAYBOOK_ASSIGNEES.includes(t.assignee) ? t.assignee : 'responsible_partner',
      dueDays: Number.isFinite(Number(t.dueDays)) ? Math.trunc(Number(t.dueDays)) : 7,
      type: asString(t.type) || null,
    }))
    .filter((t) => t.title || Boolean(opts && opts.keepUntitled));
  return { trigger, stageType: trigger === 'stage_added' ? asString(m.stageType) : '', tasks };
}

/**
 * The idempotence key of one created task: `<playbook code>:<trigger ref>:<i>`,
 * where the trigger ref is `case` for case_created and `stage:<element id>` for
 * stage_added. `Task_caseId_playbookKey_key` (189 A.0) refuses a second row
 * with the same key on the same case — that unique IS the "not twice".
 * @param {unknown} code
 * @param {unknown} triggerRef
 * @param {unknown} index
 * @returns {string}
 */
export function playbookKey(code, triggerRef, index) {
  return `${asString(code)}:${asString(triggerRef)}:${Number(index) || 0}`;
}

// ── 190 §C Bento templates ──────────────────────────────────────────────────
//
// A `bento_template` row's meta: its COLUMNS (code + label, ordered) and the
// SURFACES it may be offered on. The three Bentos are NOT interchangeable —
// `user` and `case` are over TASKS, `list` is over CASES (ruling 190 (3) and
// C.3): a template declares which of the three may offer it, so a firm cannot
// end up with "IT / accounting / graphics" columns over a list of matters.
//
// 196 §A [RULED, Laurent 2026-09-19] — A COLUMN IS NAMED IN THE APP'S THREE
// LANGUAGES, in the SAME spelling the model's own name uses (`label` = en,
// `labelFr`, `labelNl` — the taxonomy row's columns), so one convention
// governs both. `label` is the one field every column saved before 196
// holds and stays required (slugged into the code when absent); the two
// others are carried only when non-empty, so an older row round-trips
// byte-identical through the editor. A reader that finds no `labelFr`
// falls back to `label` — never to the code, never to empty
// (`bentoColumnLabel`).

/** @typedef {{ code: string, label: string, labelFr?: string, labelNl?: string }} BentoColumn */
/** @typedef {{ user: boolean, case: boolean, list: boolean }} BentoSurfaces */
/** 201 §B — `defaults`: the places this model is the FIRM'S DEFAULT for (one model per place, the server keeps it so); `impose`: while on, the default WINS over a personal pick on every place it is the default of. */
/** @typedef {{ columns: BentoColumn[], surfaces: BentoSurfaces, fields: string[], allMatters: boolean, defaults: BentoSurfaces, impose: boolean }} BentoTemplateMeta */
/** @typedef {'user'|'case'|'list'} BentoSurface */

/** @type {readonly BentoSurface[]} */
export const BENTO_SURFACES = ['user', 'case', 'list'];

// 190 glance round 2 [C], RULED (Laurent): "ideally one should chose what to
// show in each Bento template (eg a Bento template about finances does not
// need the same info as a template about procedure)". A LIST-surface template
// names the FIELDS its case card shows under the ref and the title — chosen
// per template, offered in the editor as a checklist. The vocabulary is the
// case list's OWN column keys (`buildMatterListCfg`, src/lib/matterListCfg.tsx
// `colsT`), never a second one: the card renders a chosen field through the
// list's own cell renderer, so a date, a chip or a badge reads on the card
// exactly as it reads in the list. `lk` is the column's header key, already
// seeded. The two the card carries by itself (ref, title) and the prospect
// badge are not offered. Custom fields join as `cf_<code>` (the columns'
// own keys, src/lib/customFieldColumns.tsx) — listed by the editor from the
// custom_field taxonomy, not here.
/** @type {readonly { k: string, lk: string, l: string }[]} */
export const BENTO_CASE_FIELDS = [
  { k: 'ctype',      lk: 'cc.col.type',            l: 'Type' },
  { k: 'ju',         lk: 'cc.col.jurisdiction',    l: 'Jurisdiction' },
  { k: 'stage',      lk: 'field.stage',            l: 'Stage' },
  { k: 'partner',    lk: 'cc.col.partner',         l: 'Partner' },
  { k: 'assigned',   lk: 'case.field.assigned',    l: 'Assigned' },
  { k: 'clients',    lk: 'cc.col.clients',         l: 'Clients' },
  { k: 'opened',     lk: 'cc.col.opened',          l: 'Opened' },
  { k: 'status',     lk: 'cc.col.status',          l: 'Status' },
  { k: 'statusdate', lk: 'cc.col.status_date',     l: 'Status date' },
  { k: 'tasks',      lk: 'cc.col.tasks',           l: 'Tasks' },
  { k: 'events',     lk: 'cc.col.events',          l: 'Events' },
  { k: 'latestinv',  lk: 'cc.col.latest_invoice',  l: 'Latest invoice' },
  // 190 glance round 3 [G], RULED — the seven counters OVER THE WHOLE MATTER
  // (the Récapitulatif's members, served on the list row as `recap`,
  // lib/matterRecap.js): the case list's own seven columns. 190 round 5 [R]
  // ("why state the obvious?"): the headers are the strip's own plain words
  // (new keys, the "· matter" suffix gone) — the residue, stated in
  // matterListCfg.tsx: on a card "Non marqué" is the whole matter while the
  // strip's follows the reader's filter. `recap_due` is round 2's "amount
  // due" — the same figure, the same function, its column re-keyed with its
  // six siblings; the `k`s stay, a saved template's `fields` name them.
  { k: 'recap_unmarked',   lk: 'cc.col.matter_unmarked',   l: 'Unmarked' },
  { k: 'recap_unbillable', lk: 'cc.col.matter_unbillable', l: 'Unbillable' },
  { k: 'recap_billable',   lk: 'cc.col.matter_billable',   l: 'Billable' },
  { k: 'recap_invoiced',   lk: 'cc.col.matter_invoiced',   l: 'Invoiced' },
  { k: 'recap_sent',       lk: 'cc.col.matter_sent',       l: 'Sent' },
  { k: 'recap_paid',       lk: 'cc.col.matter_paid',       l: 'Paid' },
  { k: 'recap_due',        lk: 'cc.col.matter_due',        l: 'Due' },
];

/**
 * The meta of a `bento_template` row, every part present and well-typed:
 * columns with a code (slugged from the label when absent) and a label,
 * duplicates by code dropped; the three surface flags as booleans; 201 §B —
 * the three DEFAULT flags as booleans, each AND-ed with its surface (a model
 * not offered on a place cannot be its default, whatever the row says), and
 * the `impose` flag.
 * @param {unknown} meta
 * @returns {BentoTemplateMeta}
 */
export function normalizeBentoTemplateMeta(meta) {
  const m = /** @type {Record<string, any>} */ (meta && typeof meta === 'object' ? meta : {});
  const seen = new Set();
  /** @type {BentoColumn[]} */
  const columns = [];
  for (const c of /** @type {any[]} */ (asArray(m.columns))) {
    const label = c && typeof c === 'object' ? asString(c.label).trim() : asString(c).trim();
    const code = c && typeof c === 'object' && asString(c.code).trim() ? slug(c.code) : slug(label);
    if (!code || seen.has(code)) continue;
    seen.add(code);
    // 196 §A — the two other languages ride along when present; absent stays absent.
    const labelFr = c && typeof c === 'object' ? asString(c.labelFr).trim() : '';
    const labelNl = c && typeof c === 'object' ? asString(c.labelNl).trim() : '';
    columns.push({ code, label: label || code, ...(labelFr ? { labelFr } : {}), ...(labelNl ? { labelNl } : {}) });
  }
  const sf = /** @type {Record<string, any>} */ (m.surfaces && typeof m.surfaces === 'object' ? m.surfaces : {});
  // [C] — the list card's fields: column keys, non-empty strings, in the
  // author's order, duplicates dropped; an unknown key is kept (a custom
  // field the reader cannot resolve simply renders nothing).
  /** @type {string[]} */
  const fields = [];
  for (const f of /** @type {any[]} */ (asArray(m.fields))) {
    const k = asString(f).trim();
    if (k && !fields.includes(k)) fields.push(k);
  }
  // 190 round 6 [S](i) — ONE per-template flag beside columns / surfaces /
  // fields: on, the list Bento on EITHER list shows cases AND collective
  // cases merged (each host already holds both lists' rows). Off by default.
  const surfaces = { user: sf.user === true, case: sf.case === true, list: sf.list === true };
  // 201 §B [RULED — Laurent, 2026-09-21: the default and the flag live in the
  // model's own meta, set in Taxonomies, never in Firm Settings]. A default
  // rides only where the model is offered: the editor shows no radio for an
  // unoffered place, and this AND makes a row that says otherwise read the
  // same. `impose` is one flag per model, read beside the default it applies to
  // (useBento: the place's default wins over the personal pick while it is on).
  const df = /** @type {Record<string, any>} */ (m.defaults && typeof m.defaults === 'object' ? m.defaults : {});
  const defaults = { user: surfaces.user && df.user === true, case: surfaces.case && df.case === true, list: surfaces.list && df.list === true };
  return { columns, surfaces, fields, allMatters: m.allMatters === true, defaults, impose: m.impose === true };
}

/**
 * 201 §B.3 — the firm's default model for a surface: the ONE offered template
 * whose meta names that place as its default, or null when the firm set none.
 * Read through `bentoTemplatesFor`, so an archived or column-less row can no
 * more be the default than it can be offered. The server keeps the count at
 * one per place (routes/taxonomies.js, the PATCH); should two rows ever say
 * so, the taxonomy's own order decides, as `templates[0]` always has.
 * @param {readonly DefRow[]} defs
 * @param {BentoSurface} surface
 * @returns {DefRow|null}
 */
export function bentoFirmDefaultFor(defs, surface) {
  return bentoTemplatesFor(defs, surface).find((d) => normalizeBentoTemplateMeta(d.meta).defaults[surface]) ?? null;
}

/**
 * 196 §A — the label a person reads for a Bento column, in the app's
 * language: the SAME cascade a definition's label takes (`customFieldLabel`
 * — the language's own, else `label`), delegated and not copied, so the
 * column and the model it belongs to resolve through one rule. A column
 * saved before 196 holds `label` alone and reads it in every language; the
 * code is reached only by a column with no label at all, which the
 * normaliser never emits.
 * @param {BentoColumn|null|undefined} column
 * @param {string} lang
 * @returns {string}
 */
export function bentoColumnLabel(column, lang) {
  return customFieldLabel(column, lang);
}

/**
 * The templates a surface may offer, in taxonomy order: unarchived rows whose
 * meta names that surface and that hold at least one column.
 * @param {readonly DefRow[]} defs
 * @param {BentoSurface} surface
 * @returns {DefRow[]}
 */
export function bentoTemplatesFor(defs, surface) {
  return (defs ?? []).filter((d) => d && !d.archived && typeof d.code === 'string' && d.code && normalizeBentoTemplateMeta(d.meta).surfaces[surface] && normalizeBentoTemplateMeta(d.meta).columns.length > 0);
}

/**
 * THE LADDER (ruling 190 (5), C.2 — Eisenhower's own, App.tsx
 * userMatrixQuadrant → utils.ts eisenQuadrant): where a card sits is MY
 * placement, else the FIRM's, else the template's FIRST column; a placement
 * naming a column the template no longer has reads as the first column too.
 * @param {number} itemId
 * @param {Readonly<Record<string, string>>} mine
 * @param {Readonly<Record<string, string>>} firm
 * @param {readonly BentoColumn[]} columns
 * @returns {string} the column code, or '' when the template has no column
 */
export function bentoColumnOf(itemId, mine, firm, columns) {
  const first = columns.length ? columns[0].code : '';
  const known = new Set(columns.map((c) => c.code));
  const own = mine[String(itemId)];
  if (typeof own === 'string' && known.has(own)) return own;
  const firms = firm[String(itemId)];
  if (typeof firms === 'string' && known.has(firms)) return firms;
  return first;
}
